"use client"

import styled from "@emotion/styled"
import { Button, ButtonBase } from "@mui/material"
import { useLocale } from "next-intl"
import Link from "next/link"
import { Key, useState } from "react"
import type { Swiper as SwiperObj } from "swiper"
import "swiper/css"
import "swiper/css/pagination"
import { Navigation, Pagination } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import { extractProperty, getNonUmbracoUrl, pageStripClick } from "utils"
import LandingCard from "./../pages/ProjectsLobby/LandingCard"
import ProjectCard from "./../pages/ProjectsLobby/ProjectCard"
import Container from "./Container"
import NewText from "./NewText/NewText"
import { Desktop } from "./common/bit/MediaQueries"
import { LeftArrow, RightArrow } from "./icons"
import { sharedColors } from "./theme/colors"
type Props = {
  data: any
  projects: any
  filters: any
  landings?: any[]
}

type ProjectProps = {
  absoluteUrl: string
  id: number
  key: string
  name: string
  url: string
  urlSegment: string
}

const StyledContainer = styled(Container)``

const StyledSwiper = styled(Swiper)`
  padding: 0;
  padding-bottom: 24px;
  padding-top: 16px;
  width: 100%;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-bottom: 24px;
  }
  .swiper-pagination {
    display: none;
  }
`

const StyledSwiperSlide = styled(SwiperSlide)`
  width: 290px;
  margin-right: 16px;
  :last-child {
    margin-right: 0;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    margin-right: 24px;
    :last-child {
      margin-right: 0;
    }
  }
`

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 60px;
  svg {
    width: 20px;
  }
`

const BulletsFlex = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`

const Bullet = styled.button<{ active: boolean }>`
  width: 6px;
  height: 6px;
  cursor: pointer;
  padding: 0;
  background-color: ${({ active, theme }) => (active ? theme.colors.propBlue : "transparent")};
  border-radius: 200px;
  border: ${({ active }) => (active ? "none" : "1px solid #000000;")};
`

const StyledArrow = styled(ButtonBase)``
type PropsBullet = {
  pagination: any
  locale: string
  swiper: SwiperObj
  activeIndex: number
}
const PaginationBullets = ({ pagination, locale, swiper, activeIndex }: PropsBullet) => {
  return (
    <BulletsFlex>
      {pagination && (
        <>
          <Desktop>
            <StyledArrow
              onClick={() => swiper.slidePrev()}
              aria-label={locale === "he" ? "לפריטים הקודמים בגלריה" : "Previous gallery items"}
            >
              {locale === "he" ? <RightArrow /> : <LeftArrow />}
            </StyledArrow>
          </Desktop>
          {pagination?.map((_: any, index: Key | null | undefined) => (
            <Bullet
              active={index === activeIndex}
              key={index}
              onClick={() => swiper?.slideTo(index as number)}
              aria-label={
                locale === "he"
                  ? `עבור לשקופית ${(index as number) + 1}`
                  : `Go to slide ${(index as number) + 1}`
              }
              aria-current={index === activeIndex ? "true" : undefined}
            />
          ))}
          <Desktop>
            <StyledArrow
              onClick={() => swiper.slideNext()}
              aria-label={locale === "he" ? "לפריטים הבאים בגלריה" : "Next gallery items"}
            >
              {locale === "he" ? <LeftArrow /> : <RightArrow />}
            </StyledArrow>
          </Desktop>
        </>
      )}
    </BulletsFlex>
  )
}

function ProjectGallerySwiper({ data, projects, filters, landings = [] }: Props) {
  const blockTitle = extractProperty(data, "blockTitle")
  const blockLink = extractProperty(data, "linkForMore")
  const linkTitle = blockLink?.[0]?.name
  const linkUrl = blockLink?.[0]?.url
  const locale = useLocale()
  const [swiper, setSwiper] = useState<SwiperObj>(Object)
  const [activeIndex, setActiveIndex] = useState(0)
  const projectsArray: ProjectProps[] = data?.properties[2]?.propertyValue.contentList || []
  if (projectsArray.length === 0) {
    return null
  }

  const pickedIds = projectsArray?.map((el: any) => el.id)

  // Build a lookup of all items by ID
  const projectsById = new Map(projects?.map((p: any) => [p.id, { ...p, kind: "project" }]) || [])
  const cityOptions = filters?.cities?.options || []
  const landingsById = new Map(
    landings?.map((l: any) => {
      const cityId = l.city?.[0]?.id
      const cityName = cityOptions.find((c: any) => c.id === cityId)?.value || ""
      return [l.id, { ...l, city: cityName, kind: "landing" }]
    }) || [],
  )

  // Merge in CMS picker order
  const orderedItems = pickedIds
    .map((id: number) => projectsById.get(id) || landingsById.get(id))
    .filter(Boolean)

  return (
    <StyledContainer>
      <NewText
        component={"h2"}
        desktopFontSize={32}
        desktopFontWeight={600}
        desktopLineHeight={"36px"}
        mobileFontSize={32}
        mobileLineHeight={"36px"}
        color={sharedColors.black}
      >
        {blockTitle}
      </NewText>
      <StyledSwiper
        slidesPerView={"auto"}
        modules={[Pagination, Navigation]}
        onSwiper={setSwiper}
        pagination
        onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
      >
        {orderedItems.map((el: any, index: any) => {
          if (el.kind === "landing") {
            return (
              <StyledSwiperSlide key={index}>
                <LandingCard landing={el} locale={locale} />
              </StyledSwiperSlide>
            )
          }
          return (
            <StyledSwiperSlide key={index}>
              <ProjectCard
                project={el}
                countries={filters.countries.options}
                statuses={filters.statuses.options}
                subsidiaries={filters.subsidiaries.options}
                key={el.projectTitle}
                toProject={""}
                locale={locale}
                cities={filters.cities.options}
                designation={filters.designations.options}
              />
            </StyledSwiperSlide>
          )
        })}
      </StyledSwiper>
      <Container withoutPadding>
        <ButtonsContainer>
          <PaginationBullets
            pagination={swiper?.pagination?.bullets}
            locale={locale}
            swiper={swiper}
            activeIndex={activeIndex}
          />
          <Desktop>
            {" "}
            <Link href={linkUrl ? getNonUmbracoUrl(linkUrl) : "/projects"}>
              <Button
                variant="outlined"
                size="large"
                role="presentation"
                tabIndex={-1}
                onClick={() => pageStripClick(linkTitle, blockTitle)}
              >
                {linkTitle || "לפרויקטים נוספים"}
              </Button>
            </Link>
          </Desktop>
        </ButtonsContainer>
      </Container>
    </StyledContainer>
  )
}

export default ProjectGallerySwiper
